// ================================================
//  MOCK DATA for SafeView (Mi Crecimiento)
//  User: María García (u01) evaluated by peers
//  Period focus: 2026-01  |  3-months-ago: 2025-10
// ================================================
import { BEHAVIOR_LEVELS_DEFAULT } from '@/data/mockCatalog';

export interface SafeResult {
  evaluatedId: string;
  evaluatorId: string;
  fundamentalId: string;
  behaviorId: string;
  score: number | 'no_obs';
  date: string; // YYYY-MM-DD
}

export interface SafePerson {
  id: string;
  name: string;
  initials: string;
}

// Current demo user
export const SAFE_DEMO_USER: SafePerson = {
  id: 'u01',
  name: 'Usuario Demo',
  initials: 'UD',
};

export const SAFE_MOCK_PEOPLE: SafePerson[] = [
  { id: 'u01', name: 'Usuario Demo',    initials: 'UD' },
  { id: 'u02', name: 'Colega Demo 2',   initials: 'C2' },
  { id: 'u03', name: 'Colega Demo 3',   initials: 'C3' },
  { id: 'u04', name: 'Colega Demo 4',   initials: 'C4' },
  { id: 'u05', name: 'Colega Demo 5',   initials: 'C5' },
  { id: 'u06', name: 'Colega Demo 6',   initials: 'C6' },
  { id: 'u07', name: 'Colega Demo 7',   initials: 'C7' },
];

// Derivados del catálogo — fuente única de verdad
export const SAFE_LEVEL_LABELS = BEHAVIOR_LEVELS_DEFAULT.map(l => l.label) as readonly string[];
export const SAFE_LEVEL_COLORS = BEHAVIOR_LEVELS_DEFAULT.map(l => l.color) as readonly string[];

// Celebration phrases per fundamental
export const SAFE_CELEBRATION_PHRASES: Record<string, string> = {
  'learner':      'Tu curiosidad y apertura al aprendizaje inspiran a quienes te rodean.',
  'player-owner': 'Tu proactividad y sentido de responsabilidad marcan la diferencia en el equipo.',
  'communicator': 'Tu claridad para comunicar y escuchar hace que todos se sientan comprendidos.',
  'team-builder': 'Cuando tú facilitas, hasta los más callados terminan aportando algo.',
};

// FA icon for each fundamental (fallback if no iconSvg)
export const SAFE_FUND_ICONS: Record<string, string> = {
  'learner':      'fa-brain',
  'player-owner': 'fa-bullseye',
  'communicator': 'fa-comments',
  'team-builder': 'fa-users',
};

// ================================================
//  MOCK EVALUATION RESULTS
//  Covers Oct 2025 → Jan 2026 (4 months of data)
// ================================================
// Scores per fundamental (avg per month):
//  learner:      Oct 2.8 | Nov 3.0 | Dec 3.3 | Jan 3.5  ← STRENGTH
//  player-owner: Oct 2.9 | Nov 3.0 | Dec 3.1 | Jan 3.1  ← MODERATE
//  communicator: Oct 2.3 | Nov 2.4 | Dec 2.5 | Jan 2.5  ← GROWTH
//  team-builder: Oct 2.0 | Nov 2.2 | Dec 2.2 | Jan 2.3  ← MAIN GROWTH

function makeEvals(
  evaluatedId: string,
  fundamentalId: string,
  behaviorId: string,
  evaluatorId: string,
  date: string,
  score: number | 'no_obs',
): SafeResult {
  return { evaluatedId, evaluatorId, fundamentalId, behaviorId, score, date };
}

export const SAFE_MOCK_RESULTS: SafeResult[] = [
  // ─── Oct 2025 ─────────────────────────────────────────
  // learner – b1,b2,b3 evaluated by 3 peers
  makeEvals('u01','learner','b1','u02','2025-10-05', 3),
  makeEvals('u01','learner','b1','u03','2025-10-08', 3),
  makeEvals('u01','learner','b2','u04','2025-10-10', 3),
  makeEvals('u01','learner','b2','u05','2025-10-12', 2),
  makeEvals('u01','learner','b3','u02','2025-10-15', 3),
  makeEvals('u01','learner','b3','u06','2025-10-18', 3),

  // player-owner – b6,b7
  makeEvals('u01','player-owner','b6','u03','2025-10-06', 3),
  makeEvals('u01','player-owner','b6','u04','2025-10-09', 3),
  makeEvals('u01','player-owner','b7','u02','2025-10-14', 3),
  makeEvals('u01','player-owner','b7','u05','2025-10-19', 2),

  // communicator – b10,b11
  makeEvals('u01','communicator','b10','u03','2025-10-07', 2),
  makeEvals('u01','communicator','b10','u06','2025-10-11', 2),
  makeEvals('u01','communicator','b11','u07','2025-10-16', 3),
  makeEvals('u01','communicator','b11','u04','2025-10-20', 2),

  // team-builder – b16,b17
  makeEvals('u01','team-builder','b16','u02','2025-10-08', 2),
  makeEvals('u01','team-builder','b16','u05','2025-10-13', 2),
  makeEvals('u01','team-builder','b17','u03','2025-10-17', 2),
  makeEvals('u01','team-builder','b17','u07','2025-10-21', 2),

  // ─── Nov 2025 ─────────────────────────────────────────
  makeEvals('u01','learner','b1','u04','2025-11-04', 3),
  makeEvals('u01','learner','b1','u06','2025-11-07', 3),
  makeEvals('u01','learner','b2','u02','2025-11-10', 3),
  makeEvals('u01','learner','b2','u05','2025-11-13', 3),
  makeEvals('u01','learner','b3','u03','2025-11-16', 3),
  makeEvals('u01','learner','b4','u07','2025-11-19', 3),

  makeEvals('u01','player-owner','b6','u05','2025-11-05', 3),
  makeEvals('u01','player-owner','b7','u03','2025-11-09', 3),
  makeEvals('u01','player-owner','b8','u06','2025-11-14', 3),
  makeEvals('u01','player-owner','b8','u07','2025-11-18', 3),

  makeEvals('u01','communicator','b10','u04','2025-11-06', 2),
  makeEvals('u01','communicator','b10','u02','2025-11-11', 3),
  makeEvals('u01','communicator','b11','u05','2025-11-15', 2),
  makeEvals('u01','communicator','b12','u06','2025-11-20', 2),

  makeEvals('u01','team-builder','b16','u03','2025-11-07', 2),
  makeEvals('u01','team-builder','b17','u04','2025-11-12', 3),
  makeEvals('u01','team-builder','b18','u07','2025-11-17', 2),

  // ─── Dec 2025 ─────────────────────────────────────────
  makeEvals('u01','learner','b1','u03','2025-12-03', 4),
  makeEvals('u01','learner','b1','u05','2025-12-06', 3),
  makeEvals('u01','learner','b2','u06','2025-12-09', 3),
  makeEvals('u01','learner','b2','u07','2025-12-12', 4),
  makeEvals('u01','learner','b3','u02','2025-12-15', 3),
  makeEvals('u01','learner','b3','u04','2025-12-18', 3),
  makeEvals('u01','learner','b4','u06','2025-12-20', 4),

  makeEvals('u01','player-owner','b6','u04','2025-12-04', 3),
  makeEvals('u01','player-owner','b7','u02','2025-12-08', 3),
  makeEvals('u01','player-owner','b8','u05','2025-12-13', 3),
  makeEvals('u01','player-owner','b9','u07','2025-12-17', 3),

  makeEvals('u01','communicator','b10','u03','2025-12-05', 3),
  makeEvals('u01','communicator','b11','u06','2025-12-10', 2),
  makeEvals('u01','communicator','b12','u04','2025-12-14', 3),
  makeEvals('u01','communicator','b13','u07','2025-12-19', 2),

  makeEvals('u01','team-builder','b16','u05','2025-12-06', 3),
  makeEvals('u01','team-builder','b17','u02','2025-12-11', 2),
  makeEvals('u01','team-builder','b18','u03','2025-12-16', 2),

  // ─── Jan 2026 ─────────────────────────────────────────
  makeEvals('u01','learner','b1','u02','2026-01-05', 4),
  makeEvals('u01','learner','b1','u04','2026-01-08', 4),
  makeEvals('u01','learner','b2','u05','2026-01-10', 3),
  makeEvals('u01','learner','b2','u06','2026-01-13', 4),
  makeEvals('u01','learner','b3','u03','2026-01-15', 3),
  makeEvals('u01','learner','b3','u07','2026-01-18', 4),
  makeEvals('u01','learner','b4','u02','2026-01-20', 3),
  makeEvals('u01','learner','b5','u04','2026-01-22', 3),

  makeEvals('u01','player-owner','b6','u05','2026-01-06', 3),
  makeEvals('u01','player-owner','b6','u03','2026-01-09', 3),
  makeEvals('u01','player-owner','b7','u06','2026-01-12', 4),
  makeEvals('u01','player-owner','b7','u07','2026-01-16', 3),
  makeEvals('u01','player-owner','b8','u02','2026-01-19', 3),
  makeEvals('u01','player-owner','b8','u04','2026-01-21', 3),

  makeEvals('u01','communicator','b10','u05','2026-01-07', 3),
  makeEvals('u01','communicator','b10','u03','2026-01-11', 2),
  makeEvals('u01','communicator','b11','u06','2026-01-14', 3),
  makeEvals('u01','communicator','b11','u07','2026-01-17', 2),
  makeEvals('u01','communicator','b12','u02','2026-01-20', 3),
  makeEvals('u01','communicator','b13','u04','2026-01-22', 2),

  makeEvals('u01','team-builder','b16','u06','2026-01-08', 2),
  makeEvals('u01','team-builder','b16','u03','2026-01-12', 3),
  makeEvals('u01','team-builder','b17','u05','2026-01-15', 3),
  makeEvals('u01','team-builder','b17','u07','2026-01-19', 2),
  makeEvals('u01','team-builder','b18','u02','2026-01-21', 2),
];

// Given feedback (surveys completed by this user)
export const SAFE_GIVEN_SURVEYS = [
  { name: 'Fundamentals Q1',       people: 4, date: '28 ene', status: 'completada' },
  { name: 'Colaboración',           people: 2, date: '25 ene', status: 'completada' },
  { name: 'Pulso Rápido · Sprint',  people: 1, date: '20 ene', status: 'completada' },
];

// ================================================
//  HELPERS
// ================================================

export function getScoresForUser(userId: string, month: string): SafeResult[] {
  return SAFE_MOCK_RESULTS.filter(
    r => r.evaluatedId === userId && r.score !== 'no_obs' && r.date.startsWith(month),
  );
}

export function getAvgByFundamental(
  userId: string,
  month: string,
): { id: string; avg: number; count: number }[] {
  const funds = ['learner', 'player-owner', 'communicator', 'team-builder'];
  return funds.map(fundId => {
    const scores = SAFE_MOCK_RESULTS.filter(
      r =>
        r.evaluatedId === userId &&
        r.fundamentalId === fundId &&
        r.score !== 'no_obs' &&
        r.date.startsWith(month),
    );
    const numScores = scores.filter(r => typeof r.score === 'number').map(r => r.score as number);
    const avg =
      numScores.length > 0
        ? Math.round((numScores.reduce((a, b) => a + b, 0) / numScores.length) * 10) / 10
        : 0;
    return { id: fundId, avg, count: numScores.length };
  }).filter(f => f.count > 0);
}

export function getOverallAvg(userId: string, month: string): number {
  const scores = SAFE_MOCK_RESULTS.filter(
    r => r.evaluatedId === userId && r.score !== 'no_obs' && r.date.startsWith(month),
  ).map(r => r.score as number);
  if (scores.length === 0) return 0;
  return Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10;
}

export function getMonthlyTrend(userId: string): { month: string; avg: number }[] {
  const months = ['2025-08', '2025-09', '2025-10', '2025-11', '2025-12', '2026-01'];
  return months.map(m => ({ month: m, avg: getOverallAvg(userId, m) }));
}

export function safeLevelFromAvg(avg: number): { idx: number; progressing: boolean; frac: number } {
  const c = Math.max(1, Math.min(4, avg));
  const base = Math.floor(c);
  const frac = c - base;
  const idx = Math.max(0, Math.min(3, base - 1));
  return { idx, progressing: frac >= 0.4 && idx < 3, frac };
}

export function pbLevelIndex(avg: number): number {
  const c = Math.max(1, Math.min(4, avg));
  return Math.max(0, Math.min(3, Math.round(c) - 1));
}

export function safeConfidence(n: number): string {
  if (n < 3) return 'Primera señal';
  if (n < 8) return 'Patrón que emerge';
  if (n < 20) return 'Patrón claro';
  return 'Patrón consolidado';
}

export function getDistribution(
  userId: string,
  fundamentalId: string,
  month: string,
): { total: number; pct: Record<number, number> } {
  const scores = SAFE_MOCK_RESULTS.filter(
    r =>
      r.evaluatedId === userId &&
      r.fundamentalId === fundamentalId &&
      r.score !== 'no_obs' &&
      r.date.startsWith(month),
  ).map(r => r.score as number);
  const total = scores.length;
  const dist: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0 };
  scores.forEach(s => { dist[s] = (dist[s] || 0) + 1; });
  return {
    total,
    pct: {
      1: total ? Math.round((dist[1] / total) * 100) : 0,
      2: total ? Math.round((dist[2] / total) * 100) : 0,
      3: total ? Math.round((dist[3] / total) * 100) : 0,
      4: total ? Math.round((dist[4] / total) * 100) : 0,
    },
  };
}

export function getSubBehaviorAvgs(
  userId: string,
  fundamentalId: string,
  month: string,
): { behaviorId: string; avg: number | null; count: number }[] {
  const fundBehaviorIds: Record<string, string[]> = {
    'learner':      ['b1','b2','b3','b4','b5'],
    'player-owner': ['b6','b7','b8','b9'],
    'communicator': ['b10','b11','b12','b13','b14','b15'],
    'team-builder': ['b16','b17','b18'],
  };
  const ids = fundBehaviorIds[fundamentalId] ?? [];
  return ids.map(bId => {
    const scores = SAFE_MOCK_RESULTS.filter(
      r =>
        r.evaluatedId === userId &&
        r.behaviorId === bId &&
        r.score !== 'no_obs' &&
        r.date.startsWith(month),
    ).map(r => r.score as number);
    const avg =
      scores.length > 0
        ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
        : null;
    return { behaviorId: bId, avg, count: scores.length };
  });
}
